import React, { useState, useEffect } from 'react';
import logo from '../../assests/images/logo.png'; // Assuming this path is correct in the user's project
import { FaBars, FaTimes } from "react-icons/fa"; // Assuming react-icons is installed
import Sidebar from './sidebar'; // Assuming Sidebar.js exists and handles mobile navigation
import { Link } from 'react-router-dom'; // Assuming react-router-dom is installed for internal navigation

// Base URL for external links. Centralized for easier management.
const BASE_EXTERNAL_URL = "https://glocybs.com";

/**
 * Dropdown component for Services.
 * Displays a list of services with descriptions, linking to external pages.
 * All links open in a new tab.
 */
const DropdownServices = ({ onMouseEnter, onMouseLeave }) => {
    return (
        // Absolute positioning for the dropdown, centered horizontally using translate-x-1/2.
        // Responsive width to adapt to different screen sizes.
        <div
            className="absolute z-20 bg-white p-6 md:p-8 rounded-lg shadow-lg
                       w-[calc(100vw-40px)] md:w-[800px] lg:w-[1000px] xl:w-[1200px]
                       left-1/2 -translate-x-1/2 overflow-x-auto text-black"
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            <div className="text-center mb-4">
                <h2 className="text-xl font-bold text-red-500">All Services</h2>
            </div>
            <div className="flex justify-center mb-6">
                {/* Explore Platform button - opens in new tab */}
                <a href={`${BASE_EXTERNAL_URL}/services`} target="_blank" rel="noopener noreferrer"
                   className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-full transition-colors duration-200">
                    Explore Platform <span className="ml-2">→</span>
                </a>
            </div>
            {/* Horizontal separator */}
            <div className="border-b-2 border-red-500 mb-6"></div>
            {/* Grid layout for services, responsive columns */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6 md:gap-8 text-left">
                {[
                    ["AI DEFENSE", "ai-defense", "Utilizes adaptive intelligence and advanced AI to forecast, identify, and stop cyber threats."],
                    ["BLOCKCHAIN SECURITY", "blockchain-security", "Strengthens your blockchain with AI-powered audits and multi-layered security, ensuring data integrity and protection from threats"],
                    ["ORGANIZATIONAL SECURITY", "organizational-security", "Provides comprehensive organizational security by protecting your data, devices, and employees against internal and external attacks."],
                    ["WEBSITE SECURITY", "website-security", "Offers comprehensive website security with multi-layered protection, and 24/7 threat monitoring."],
                    ["DATABASE SECURITY", "database-security", "Deliver powerful database security solutions that protect sensitive information with enhanced protection."],
                    ["DATA LOSS PROTECTION (DLP)", "data-loss-protection", "Protect essential information from unauthorized access and accidental breaches."],
                    ["CYBER SECURITY INSURANCE BROKERAGE", "cyber-security-insurance", "Secure your business with cybersecurity insurance brokerage, protecting against digital risks and financial losses."],
                    ["MANAGED DETECTION AND RESPONSE", "managed-detection-response", "Expert threat detection and immediate response."],
                    ["IDENTITY AND ACCESS MANAGEMENT (IAM)", "identity-access-management", "Manage access securely with our Identity and Access Management service."],
                    ["DIGITAL FORENSICS", "digital-forensics", "Investigating and interpreting digital footprints to secure your operations."]
                ].map(([title, path, desc]) => (
                    <div key={path}>
                        {/* Service link - opens in new tab */}
                        <a href={`${BASE_EXTERNAL_URL}/services/${path}`} target="_blank" rel="noopener noreferrer"
                           className="font-bold text-gray-800 mb-1 hover:text-red-500 transition-colors duration-200 block text-lg">
                            {title}
                        </a>
                        <p className="text-gray-600 text-sm">{desc}</p>
                    </div>
                ))}
            </div>
        </div>
    );
};

/**
 * Dropdown component for "BIQUA" section.
 * Displays various BIQUA-related topics, linking to external pages.
 * All links open in a new tab.
 */
const DropdownBiqua = ({ onMouseEnter, onMouseLeave }) => {
    return (
        // Absolute positioning for the dropdown, centered horizontally.
        // Responsive width for better adaptation.
        <div
            className="absolute z-50 bg-white p-6 rounded-lg shadow-lg
                       w-[calc(100vw-40px)] md:w-[700px] lg:w-[900px] xl:w-[1000px]
                       left-1/2 -translate-x-1/2 text-black overflow-x-auto"
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 text-left"> {/* Changed space-x to gap */}
                <div>
                    <a href={`${BASE_EXTERNAL_URL}/biqua/blockchain`} target="_blank" rel="noopener noreferrer" className="text-red-500 font-bold pb-2 block">Blockchain</a>
                    <div className='border-t-2 border-blue-600 w-full mt-[5%]'></div>
                    <p className="text-sm text-left mt-[10%]">Allows for the verification of data without revealing the data itself, enhancing privacy.</p>
                    <ul className="mt-4 space-y-3 font-bold"> {/* Adjusted space-y */}
                        <li><a href={`${BASE_EXTERNAL_URL}/biqua/blockchain/consensus-mechanisms`} target="_blank" rel="noopener noreferrer">Consensus Mechanisms</a></li>
                        <li><a href={`${BASE_EXTERNAL_URL}/biqua/blockchain/smart-contract-security`} target="_blank" rel="noopener noreferrer">Smart Contract Security</a></li>
                        <li><a href={`${BASE_EXTERNAL_URL}/biqua/blockchain/encryption-privacy`} target="_blank" rel="noopener noreferrer">Encryption and Privacy</a></li>
                        <li><a href={`${BASE_EXTERNAL_URL}/biqua/blockchain/secure-key-management`} target="_blank" rel="noopener noreferrer">Secure Key Management</a></li>
                        <li><a href={`${BASE_EXTERNAL_URL}/biqua/blockchain/network-security`} target="_blank" rel="noopener noreferrer">Network Security</a></li>
                    </ul>
                </div>
                <div>
                    <a href={`${BASE_EXTERNAL_URL}/biqua/innovation`} target="_blank" rel="noopener noreferrer" className="text-red-500 font-bold pb-2 block">Innovation</a>
                    <div className='border-t-2 border-blue-600 w-full mt-[5%]'></div>
                    <p className="text-sm text-left mt-[10%]">AI-driven systems can learn and recognize normal user behavior patterns and detect anomalies that may indicate a security breach.</p>
                    <ul className="mt-4 space-y-3 font-bold"> {/* Adjusted space-y */}
                        <li><a href={`${BASE_EXTERNAL_URL}/biqua/innovation/behavioural-analysis`} target="_blank" rel="noopener noreferrer">Behavioural Analysis</a></li>
                        <li><a href={`${BASE_EXTERNAL_URL}/biqua/innovation/post-quantum-algorithms`} target="_blank" rel="noopener noreferrer">Post-Quantum Algorithms</a></li>
                        <li><a href={`${BASE_EXTERNAL_URL}/biqua/innovation/decentralized-autonomous-organizations`} target="_blank" rel="noopener noreferrer">Decentralized Autonomous Organizations (DAOs)</a></li>
                        <li><a href={`${BASE_EXTERNAL_URL}/biqua/innovation/ueba`} target="_blank" rel="noopener noreferrer">User and Entity Behaviour Analytics (UEBA)</a></li>
                    </ul>
                </div>
                <div>
                    <a href={`${BASE_EXTERNAL_URL}/biqua/quantum-computing`} target="_blank" rel="noopener noreferrer" className="text-red-500 font-bold pb-2 block">Quantum Computing</a>
                    <div className='border-t-2 border-blue-600 w-full mt-[5%]'></div>
                    <p className="text-sm text-left mt-[10%]">Research and develop a quantum internet infrastructure for ultra-secure communication channels using quantum entanglement.</p>
                    <ul className="mt-4 space-y-3 font-bold"> {/* Adjusted space-y */}
                        <li><a href={`${BASE_EXTERNAL_URL}/biqua/quantum-computing/quantum-resistant-encryption`} target="_blank" rel="noopener noreferrer">Quantum-Resistant Encryption</a></li>
                        <li><a href={`${BASE_EXTERNAL_URL}/biqua/quantum-computing/quantum-secure-networks`} target="_blank" rel="noopener noreferrer">Quantum-Secure Networks</a></li>
                        <li><a href={`${BASE_EXTERNAL_URL}/biqua/quantum-computing/advanced-threat-detection`} target="_blank" rel="noopener noreferrer">Advanced Threat Detection</a></li>
                        <li><a href={`${BASE_EXTERNAL_URL}/biqua/quantum-computing/optimization-security-protocols`} target="_blank" rel="noopener noreferrer">Optimization of Security Protocols</a></li>
                        <li><a href={`${BASE_EXTERNAL_URL}/biqua/quantum-computing/secure-data-storage`} target="_blank" rel="noopener noreferrer">Secure Data Storage</a></li>
                    </ul>
                </div>
                <div>
                    <a href={`${BASE_EXTERNAL_URL}/biqua/artificial-intelligence`} target="_blank" rel="noopener noreferrer" className="text-red-500 font-bold pb-2 block">Artificial Intelligence</a>
                    <div className='border-t-2 border-blue-600 w-full mt-[5%]'></div>
                    <p className="text-sm text-left mt-[10%]">AI can improve the accuracy and security of biometric authentication methods, such as facial recognition, fingerprint scanning, and voice recognition, reducing the risk of unauthorized access.</p>
                    <ul className="mt-4 space-y-3 font-bold"> {/* Adjusted space-y */}
                        <li><a href={`${BASE_EXTERNAL_URL}/biqua/artificial-intelligence/enhanced-access-control`} target="_blank" rel="noopener noreferrer">Enhanced Access Control and Authentication</a></li>
                        <li><a href={`${BASE_EXTERNAL_URL}/biqua/artificial-intelligence/fraud-detection`} target="_blank" rel="noopener noreferrer">Fraud Detection and Prevention</a></li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

/**
 * Dropdown component for "Quantum Computing" section.
 * Displays various Quantum Computing-related topics, linking to external pages.
 * All links open in a new tab.
 */
const DropdownQuantumComputing = ({ onMouseEnter, onMouseLeave }) => {
    return (
        // Absolute positioning for the dropdown, centered horizontally.
        // Responsive width for better adaptation.
        <div
            className="absolute z-50 bg-white p-6 rounded-lg shadow-lg
                       w-[calc(100vw-40px)] md:w-[800px] lg:w-[1000px] xl:w-[1200px]
                       left-1/2 -translate-x-1/2 text-black overflow-x-auto"
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 md:gap-8 text-left"> {/* Changed space-x to gap */}
                <div>
                    <a href={`${BASE_EXTERNAL_URL}/quantum-computing/authentication`} target="_blank" rel="noopener noreferrer" className="text-red-500 font-bold pb-2 block">Authentication and Identity Management</a>
                    <p className='border-b-4 border-blue-500 mt-[12%]'></p>
                    <p className="text-sm text-left mt-[10%]">Use quantum computing to enhance biometric authentication methods, making them more secure and harder to spoof.</p>
                    <ul className="mt-4 space-y-3 font-bold"> {/* Adjusted space-y */}
                        <li><a href={`${BASE_EXTERNAL_URL}/quantum-computing/authentication/quantum-biometrics`} target="_blank" rel="noopener noreferrer">Quantum Biometrics</a></li>
                        <li><a href={`${BASE_EXTERNAL_URL}/quantum-computing/authentication/decentralized-identity-verification`} target="_blank" rel="noopener noreferrer">Decentralized Identity Verification</a></li>
                    </ul>
                </div>
                <div>
                    <a href={`${BASE_EXTERNAL_URL}/quantum-computing/cryptanalysis`} target="_blank" rel="noopener noreferrer" className="text-red-500 font-bold pb-2 block">Cryptanalysis</a>
                    <p className='border-b-4 border-blue-500 mt-[12%]'></p>
                    <p className="text-sm text-left mt-[10%]">Investigate quantum algorithms, such as Shor's algorithm, to understand their implications for breaking classical cryptographic systems and develop countermeasures.</p>
                    <ul className="mt-4 space-y-3 font-bold"> {/* Adjusted space-y */}
                        <li><a href={`${BASE_EXTERNAL_URL}/quantum-computing/cryptanalysis/quantum-cryptanalysis-research`} target="_blank" rel="noopener noreferrer">Quantum Cryptanalysis Research</a></li>
                        <li><a href={`${BASE_EXTERNAL_URL}/quantum-computing/cryptanalysis/quantum-random-number-generation`} target="_blank" rel="noopener noreferrer">Quantum Random Number Generation (QRNG)</a></li>
                    </ul>
                </div>
                <div>
                    <a href={`${BASE_EXTERNAL_URL}/quantum-computing/incident-response`} target="_blank" rel="noopener noreferrer" className="text-red-500 font-bold pb-2 block">Incident Response and Recovery</a>
                    <p className='border-b-4 border-blue-500'></p>
                    <p className="text-sm text-left mt-[10%]">Test the resilience of cybersecurity systems against potential quantum attacks to ensure they can withstand future threats.</p>
                    <ul className="mt-4 space-y-3 font-bold"> {/* Adjusted space-y */}
                        <li><a href={`${BASE_EXTERNAL_URL}/quantum-computing/incident-response/quantum-enhanced-forensics`} target="_blank" rel="noopener noreferrer">Quantum-Enhanced Forensics</a></li>
                        <li><a href={`${BASE_EXTERNAL_URL}/quantum-computing/incident-response/quantum-resilience-testing`} target="_blank" rel="noopener noreferrer">Quantum Resilience Testing</a></li>
                    </ul>
                </div>
                <div>
                    <a href={`${BASE_EXTERNAL_URL}/quantum-computing/secure-software-development`} target="_blank" rel="noopener noreferrer" className="text-red-500 font-bold pb-2 block">Secure Software Development</a>
                    <p className='border-b-4 border-blue-500'></p>
                    <p className="text-sm text-left mt-[10%]">Use quantum computing for advanced static and dynamic code analysis to identify vulnerabilities and security flaws in software.</p>
                    <ul className="mt-4 space-y-3 font-bold"> {/* Adjusted space-y */}
                        <li><a href={`${BASE_EXTERNAL_URL}/quantum-computing/secure-software-development/quantum-enhanced-code-analysis`} target="_blank" rel="noopener noreferrer">Quantum-Enhanced Code Analysis</a></li>
                        <li><a href={`${BASE_EXTERNAL_URL}/quantum-computing/secure-software-development/quantum-proof-software-design`} target="_blank" rel="noopener noreferrer">Quantum-Proof Software Design</a></li>
                    </ul>
                </div>
                <div>
                    <a href={`${BASE_EXTERNAL_URL}/quantum-computing/training-education`} target="_blank" rel="noopener noreferrer" className="text-red-500 font-bold pb-2 block">Training and Education</a>
                    <p className='border-b-4 border-blue-500 mt-[12%]'></p>
                    <p className="text-sm text-left mt-[10%]">Provide specialized training for cybersecurity professionals on quantum computing and its implications for security.</p>
                    <ul className="mt-4 space-y-3 font-bold"> {/* Adjusted space-y */}
                        <li><a href={`${BASE_EXTERNAL_URL}/quantum-computing/training-education/quantum-cybersecurity-training`} target="_blank" rel="noopener noreferrer">Quantum Cybersecurity Training</a></li>
                        <li><a href={`${BASE_EXTERNAL_URL}/quantum-computing/training-education/collaborative-research-initiatives`} target="_blank" rel="noopener noreferrer">Collaborative Research Initiatives</a></li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

/**
 * Dropdown component for "More" section.
 * Displays additional topics/pages, linking to external pages.
 * All links open in a new tab.
 */
const DropdownMore = ({ onMouseEnter, onMouseLeave }) => {
    return (
        // Absolute positioning for the dropdown, centered horizontally.
        <div
            className="absolute z-50 bg-white p-6 rounded-lg shadow-lg w-[250px] font-bold
                       left-1/2 -translate-x-1/2 text-black overflow-x-auto"
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            <h2 className="text-red-500 font-bold text-xl mb-2">More</h2>
            <div className="h-1 bg-gradient-to-r from-red-500 to-blue-500 mb-6"></div>
            <div className="grid grid-cols-1 gap-4 text-left"> {/* Removed md:grid-cols-1 as it's redundant */}
                <div>
                    <ul className="space-y-4"> {/* Adjusted space-y */}
                        <li><a href={`${BASE_EXTERNAL_URL}/more/AIPredictiveAnalytics`} target="_blank" rel="noopener noreferrer" className="text-gray-800 hover:text-red-500 block">AI-Driven Predictive Analytics</a></li>
                        <li><a href={`${BASE_EXTERNAL_URL}/more/AIPoweredSOCs`} target="_blank" rel="noopener noreferrer" className="text-gray-800 hover:text-red-500 block">AI-Powered Security Operations Centers (SOCs)</a></li>
                        <li><a href={`${BASE_EXTERNAL_URL}/more/AIInsiderThreatDetection`} target="_blank" rel="noopener noreferrer" className="text-gray-800 hover:text-red-500 block">AI for Insider Threat Detection</a></li>
                        <li><a href={`${BASE_EXTERNAL_URL}/more/EthicalAICybersecurity`} target="_blank" rel="noopener noreferrer" className="text-gray-800 hover:text-red-500 block">Ethical and Responsible AI Use in Cybersecurity</a></li>
                        <li><a href={`${BASE_EXTERNAL_URL}/more/FutureTrends`} target="_blank" rel="noopener noreferrer" className="text-gray-800 hover:text-red-500 block">Future Trends and Innovations in AI-Cybersecurity Integration</a></li>
                        <li><a href={`${BASE_EXTERNAL_URL}/more/careers`} target="_blank" rel="noopener noreferrer" className="text-gray-800 hover:text-red-500 block">Careers</a></li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

/**
 * Dropdown component for "Contact" section.
 * Displays various contact-related links, linking to external pages.
 * All links open in a new tab.
 */
const DropdownContact = ({ onMouseEnter, onMouseLeave }) => {
    return (
        // Absolute positioning for the dropdown, centered horizontally.
        <div
            className="absolute z-50 bg-white p-6 rounded-lg shadow-lg w-[250px] font-bold
                       left-1/2 -translate-x-1/2 text-black overflow-x-auto"
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            <h2 className="text-red-500 font-bold text-xl mb-2">Contact</h2>
            <div className="h-1 bg-gradient-to-r from-red-500 to-blue-500 mb-6"></div>
            <div className="grid grid-cols-1 gap-4 text-left"> {/* Removed md:grid-cols-1 as it's redundant */}
                <div>
                    <ul className="space-y-4"> {/* Adjusted space-y */}
                        <li><a href={`${BASE_EXTERNAL_URL}/contact/leadership`} target="_blank" rel="noopener noreferrer" className="text-gray-800 hover:text-red-500 block">Our Teams</a></li>
                        <li><a href={`${BASE_EXTERNAL_URL}/contact/information`} target="_blank" rel="noopener noreferrer" className="text-gray-800 hover:text-red-500 block">Contact Information</a></li> {/* Added specific path */}
                        <li><a href={`${BASE_EXTERNAL_URL}/contact/form`} target="_blank" rel="noopener noreferrer" className="text-gray-800 hover:text-red-500 block">Contact Form</a></li>
                        <li><a href={`${BASE_EXTERNAL_URL}/contact/faq`} target="_blank" rel="noopener noreferrer" className="text-gray-800 hover:text-red-500 block">FAQ Section</a></li> {/* Added specific path */}
                        <li><a href={`${BASE_EXTERNAL_URL}/contact/feedback`} target="_blank" rel="noopener noreferrer" className="text-gray-800 hover:text-red-500 block">Feedback and Support</a></li> {/* Added specific path */}
                        <li><a href={`${BASE_EXTERNAL_URL}/contact/privacy`} target="_blank" rel="noopener noreferrer" className="text-gray-800 hover:text-red-500 block">Privacy and Data Protection Notice</a></li> {/* Added specific path */}
                    </ul>
                </div>
            </div>
        </div>
    );
};


/**
 * Main Navbar component.
 * Handles desktop dropdowns, mobile sidebar toggle, and scroll-based styling.
 */
const Navbar = () => {
    // State to manage visibility of dropdowns on hover
    const [isServicesHovered, setIsServicesHovered] = useState(false);
    const [isBiquaHovered, setIsBiquaHovered] = useState(false);
    const [isQuantumComputingHovered, setIsQuantumComputingHovered] = useState(false);
    const [isMoreHovered, setIsMoreHovered] = useState(false);
    const [isContactHovered, setIsContactHovered] = useState(false);
    // State to manage mobile sidebar visibility
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    // State to manage navbar styling based on scroll position
    const [isScrolled, setIsScrolled] = useState(false);

    // Close all dropdowns when sidebar opens
    useEffect(() => {
        if (isSidebarOpen) {
            setIsServicesHovered(false);
            setIsBiquaHovered(false);
            setIsQuantumComputingHovered(false);
            setIsMoreHovered(false);
            setIsContactHovered(false);
        }
    }, [isSidebarOpen]);

    // Mouse event handlers for dropdowns
    const handleServicesMouseEnter = () => setIsServicesHovered(true);
    const handleServicesMouseLeave = () => setIsServicesHovered(false);
    const handleBiquaMouseEnter = () => setIsBiquaHovered(true);
    const handleBiquaMouseLeave = () => setIsBiquaHovered(false);
    const handleQuantumComputingMouseEnter = () => setIsQuantumComputingHovered(true);
    const handleQuantumComputingMouseLeave = () => setIsQuantumComputingHovered(false);
    const handleMoreMouseEnter = () => setIsMoreHovered(true);
    const handleMoreMouseLeave = () => setIsMoreHovered(false);
    const handleContactMouseEnter = () => setIsContactHovered(true);
    const handleContactMouseLeave = () => setIsContactHovered(false);

    // Function to toggle sidebar visibility
    const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

    // Effect to add/remove scroll event listener for navbar styling
    useEffect(() => {
        const handleScroll = () => setIsScrolled(window.scrollY > 50);
        window.addEventListener('scroll', handleScroll);
        // Cleanup function to remove event listener on component unmount
        return () => window.removeEventListener('scroll', handleScroll);
    }, []); // Empty dependency array ensures this runs once on mount and cleans up on unmount

    return (
        <div className='flex justify-center w-full'>
            {/* Fixed navbar container with dynamic background and shadow based on scroll */}
            <div className={`fixed top-0 left-0 right-0 z-[100] flex justify-center transition-all duration-500 ${
                isScrolled
                    ? 'bg-slate-950/95 backdrop-blur-xl border-b border-gray-700/30 shadow-2xl shadow-blue-500/10'
                    : 'bg-slate-950/90 backdrop-blur-lg border-b border-gray-800/30'
            }`}>
                {/* Background pattern overlay */}
                <div className="absolute inset-0 opacity-5">
                    <svg className="w-full h-full" viewBox="0 0 400 100" preserveAspectRatio="xMidYMid slice">
                        <defs>
                            <pattern id="navbar-grid" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                                <circle cx="20" cy="20" r="0.5" fill="currentColor" className="text-blue-400">
                                    <animate attributeName="opacity" values="0.3;0.6;0.3" dur="3s" repeatCount="indefinite" />
                                </circle>
                            </pattern>
                        </defs>
                        <rect width="100%" height="100%" fill="url('#navbar-grid')" />
                    </svg>
                </div>

                {/* Main content wrapper for the navbar */}
                <div className='flex flex-col relative z-10 w-full'>
                    <nav className="relative container mx-auto p-2 py-4 flex justify-between items-center z-[100] max-w-7xl px-4 sm:px-6">
                        {/* Logo and site title */}
                        <div className='flex items-center font-extrabold text-[20px] sm:text-[23px] flex-shrink-0'>
                            {/* Logo link - opens in new tab */}
                            <a href={BASE_EXTERNAL_URL} target="_blank" rel="noopener noreferrer" className='flex group'>
                                <img src={logo} alt="G-QBS" className="h-8 sm:h-10 pr-2 transition-transform duration-300 group-hover:scale-110" />
                                <p className='pt-1 text-white transition-colors duration-300 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-blue-400 group-hover:to-cyan-400'>
                                    GLO<span className='text-cyan-500'>CYBS</span>
                                </p>
                            </a>
                        </div>

                        {/* Desktop Navigation */}
                        <div className="flex-1 hidden md:flex justify-center mx-4 lg:mx-8"> {/* Adjusted mx for better responsiveness */}
                            <ul className="flex space-x-4 lg:space-x-6"> {/* Adjusted space-x for better responsiveness */}
                                {/* Services Dropdown */}
                                <div
                                    className="relative group"
                                    onMouseEnter={handleServicesMouseEnter}
                                    onMouseLeave={handleServicesMouseLeave}
                                >
                                    <li>
                                        {/* Internal Link for 'Services' main item */}
                                        <Link
                                            to="/services"
                                            className="text-gray-300 hover:text-blue-400 transition-all duration-300 relative py-2 px-3 rounded-lg hover:bg-blue-500/10 group-hover:scale-105 whitespace-nowrap"
                                        >
                                            Services
                                            {/* Underline effect on hover */}
                                            <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-blue-400 to-cyan-400 transition-all duration-300 group-hover:w-full"></span>
                                        </Link>
                                    </li>
                                    {isServicesHovered && (
                                        <div className="absolute top-full left-1/2 -translate-x-1/2 z-20 animate-fadeInDown"> {/* Centered dropdown */}
                                            <DropdownServices onMouseEnter={handleServicesMouseEnter} onMouseLeave={handleServicesMouseLeave} />
                                        </div>
                                    )}
                                </div>
                                {/* BIQUA Dropdown */}
                                <div
                                    className="relative group"
                                    onMouseEnter={handleBiquaMouseEnter}
                                    onMouseLeave={handleBiquaMouseLeave}
                                >
                                    <li>
                                        {/* Internal Link for 'BIQUA' main item */}
                                        <Link
                                            to="/biqua"
                                            className="text-gray-300 hover:text-purple-400 transition-all duration-300 relative py-2 px-3 rounded-lg hover:bg-purple-500/10 group-hover:scale-105 cursor-pointer whitespace-nowrap"
                                        >
                                            BIQUA
                                            <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-purple-400 to-pink-400 transition-all duration-300 group-hover:w-full"></span>
                                        </Link>
                                    </li>
                                    {isBiquaHovered && (
                                        <div className="absolute top-full left-1/2 -translate-x-1/2 z-50 animate-fadeInDown"> {/* Centered dropdown */}
                                            <DropdownBiqua onMouseEnter={handleBiquaMouseEnter} onMouseLeave={handleBiquaMouseLeave} />
                                        </div>
                                    )}
                                </div>
                                {/* Quantum Computing Dropdown */}
                                <div
                                    className="relative group"
                                    onMouseEnter={handleQuantumComputingMouseEnter}
                                    onMouseLeave={handleQuantumComputingMouseLeave}
                                >
                                    <li>
                                        {/* Internal Link for 'Quantum Computing' main item */}
                                        <Link
                                            to="/quantumcomputing"
                                            className="text-gray-300 hover:text-cyan-400 transition-all duration-300 relative py-2 px-3 rounded-lg hover:bg-cyan-500/10 group-hover:scale-105 whitespace-nowrap"
                                        >
                                            Quantum Computing
                                            <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-cyan-400 to-blue-400 transition-all duration-300 group-hover:w-full"></span>
                                        </Link>
                                    </li>
                                    {isQuantumComputingHovered && (
                                        <div className="absolute top-full left-1/2 -translate-x-1/2 z-50 animate-fadeInDown"> {/* Centered dropdown */}
                                            <DropdownQuantumComputing onMouseEnter={handleQuantumComputingMouseEnter} onMouseLeave={handleQuantumComputingMouseLeave} />
                                        </div>
                                    )}
                                </div>
                                {/* More Dropdown */}
                                <div
                                    className="relative group"
                                    onMouseEnter={handleMoreMouseEnter}
                                    onMouseLeave={handleMoreMouseLeave}
                                >
                                    <li>
                                        {/* Placeholder link for 'More' main item. This acts as a trigger for the dropdown. */}
                                        <a href="#" className="text-gray-300 hover:text-green-400 transition-all duration-300 relative py-2 px-3 rounded-lg hover:bg-green-500/10 group-hover:scale-105 whitespace-nowrap">
                                            More
                                            <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-green-400 to-emerald-400 transition-all duration-300 group-hover:w-full"></span>
                                        </a>
                                    </li>
                                    {isMoreHovered && (
                                        <div className="absolute top-full left-1/2 -translate-x-1/2 z-50 animate-fadeInDown"> {/* Centered dropdown */}
                                            <DropdownMore onMouseEnter={handleMoreMouseEnter} onMouseLeave={handleMoreMouseLeave} />
                                        </div>
                                    )}
                                </div>
                                {/* Contact Dropdown */}
                                <div
                                    className="relative group"
                                    onMouseEnter={handleContactMouseEnter}
                                    onMouseLeave={handleContactMouseLeave}
                                >
                                    <li>
                                        {/* Placeholder link for 'Contact' main item. This acts as a trigger for the dropdown. */}
                                        <a href="#" className="text-gray-300 hover:text-orange-400 transition-all duration-300 relative py-2 px-3 rounded-lg hover:bg-orange-500/10 group-hover:scale-105 whitespace-nowrap">
                                            Contact
                                            <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-orange-400 to-red-400 transition-all duration-300 group-hover:w-full"></span>
                                        </a>
                                    </li>
                                    {isContactHovered && (
                                        <div className="absolute top-full left-1/2 -translate-x-1/2 z-50 animate-fadeInDown"> {/* Centered dropdown */}
                                            <DropdownContact onMouseEnter={handleContactMouseEnter} onMouseLeave={handleContactMouseLeave} />
                                        </div>
                                    )}
                                </div>
                            </ul>
                        </div>

                        {/* Mobile Navigation Button */}
                        <div className='flex items-center space-x-2'>
                            <div className="md:hidden flex items-center justify-center z-[110]">
                                <button
                                    onClick={toggleSidebar}
                                    className={`text-xl sm:text-2xl text-gray-300 hover:text-blue-400 transition-all duration-300 p-2 sm:p-3 rounded-lg hover:bg-blue-500/20 border border-transparent hover:border-blue-500/30 ml-2 ${
                                        isSidebarOpen ? 'text-blue-400 bg-blue-500/20 border-blue-500/30' : ''
                                    }`}
                                    aria-label={isSidebarOpen ? "Close menu" : "Open menu"}
                                >
                                    {!isSidebarOpen ? <FaBars /> : <FaTimes />}
                                </button>
                            </div>
                        </div>
                    </nav>
                    {/* Gradient line */}
                    <div className={`bg-gradient-to-r from-blue-500 via-purple-500 to-cyan-500 w-full h-1 transition-all duration-500 ${
                        isScrolled ? 'opacity-80' : 'opacity-60'
                    }`}></div>
                </div>
            </div>
            {/* Sidebar component - conditionally rendered and positioned */}
            <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />

            {/* Custom CSS for animations and mobile viewport fix */}
            <style>{`
                @keyframes fadeInDown {
                    from {
                        opacity: 0;
                        transform: translateY(-10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .animate-fadeInDown {
                    animation: fadeInDown 0.3s ease-out;
                }

                /* Mobile viewport fix for horizontal scrolling */
                @media (max-width: 768px) {
                    body {
                        -webkit-overflow-scrolling: touch;
                        overflow-x: hidden;
                    }
                }
            `}</style>
        </div>
    );
}

export default Navbar;
